(() => {
  function randomIndex(max) {
    if (max <= 1) return 0;

    if (window.crypto && window.crypto.getRandomValues) {
      const values = new Uint32Array(1);
      window.crypto.getRandomValues(values);
      return values[0] % max;
    }

    return Math.floor(Math.random() * max);
  }

  function shuffle(items) {
    for (let i = items.length - 1; i > 0; i -= 1) {
      const j = randomIndex(i + 1);
      [items[i], items[j]] = [items[j], items[i]];
    }
    return items;
  }

  function getProductId(item) {
    return item?.dataset?.mdProductId || item?.querySelector?.('product-card[data-product-id]')?.dataset.productId || '';
  }

  function getCandidateId(candidate) {
    return candidate?.id || '';
  }

  function avoidSameFirstProduct(section, selected) {
    if (selected.length < 2) return;

    const sectionId = section.dataset.mdRandomSectionId || 'default';
    const storageKey = `md-random-featured-first:${sectionId}`;

    try {
      const previousFirst = window.sessionStorage.getItem(storageKey);
      const currentFirst = getCandidateId(selected[0]);

      if (previousFirst && currentFirst === previousFirst) {
        const swapIndex = 1 + randomIndex(selected.length - 1);
        [selected[0], selected[swapIndex]] = [selected[swapIndex], selected[0]];
      }

      const nextFirst = getCandidateId(selected[0]);
      if (nextFirst) window.sessionStorage.setItem(storageKey, nextFirst);
    } catch (error) {
      // sessionStorage can be unavailable in restrictive browser modes.
    }
  }

  function materializeCandidate(candidate) {
    if (candidate.node) return candidate.node;

    const node = candidate.template?.content?.firstElementChild;
    return node ? node.cloneNode(true) : null;
  }

  function injectVariantMeasurement(section) {
    section.querySelectorAll('[data-md-unit-price-template]').forEach((template) => {
      const item = template.closest('.grid-item');
      if (!item) return;

      const price = item.querySelector('.card-footer .price');
      if (!price) return;

      const fragment = template.content.cloneNode(true);

      if (price.querySelector('.unit-price')) {
        fragment.querySelector('.unit-price')?.remove();
      }

      const measurement = fragment.querySelector('[data-md-variant-measurement]');
      if (measurement && measurement.children.length > 0) {
        price.appendChild(fragment);
      }

      template.remove();
    });
  }

  function destroyFlickityIfNeeded(section) {
    const carousel = section.querySelector('carousel-slider');
    if (!carousel || !carousel.flickity) return { carousel, wasInitialized: false };

    carousel.flickity.destroy();
    carousel.flickity = null;
    return { carousel, wasInitialized: true };
  }

  function syncNavigationState(navigation) {
    const flickity = navigation?.flickity;
    if (!flickity || navigation.wrap) return;

    const buttonPrev = navigation.buttonPrev || navigation.querySelector('.js-prev');
    const buttonNext = navigation.buttonNext || navigation.querySelector('.js-next');

    navigation.enableButton?.(buttonPrev);
    navigation.enableButton?.(buttonNext);

    if (!flickity.slides[flickity.selectedIndex - 1]) {
      navigation.disableButton?.(buttonPrev);
    }

    if (!flickity.slides[flickity.selectedIndex + 1]) {
      navigation.disableButton?.(buttonNext);
    }
  }

  function reconnectNavigation(section, carousel) {
    if (!carousel?.flickity || !carousel.id) return;

    section.querySelectorAll('carousel-navigation').forEach((navigation) => {
      if (navigation.getAttribute('for') !== carousel.id) return;

      // carousel-navigation click handlers use this.flickity dynamically.
      // Point them at the new instance created after randomizing the products.
      navigation.flickity = carousel.flickity;

      if (navigation.buttonPrev || navigation.buttonNext) {
        carousel.flickity.on('select', () => syncNavigationState(navigation));
        syncNavigationState(navigation);
      }
    });
  }

  function reinitializeFlickity(section, carousel, wasInitialized) {
    if (!wasInitialized || !carousel || typeof carousel.initialize !== 'function') return;

    carousel.initialize();
    reconnectNavigation(section, carousel);
  }

  function init(section) {
    if (!section || section.dataset.mdRandomFeaturedReady === 'true') return;

    // Normally this runs before carousel-slider.js initializes the custom element.
    // The destroy/reconnect path remains as a safe fallback for theme-editor reloads.
    const { carousel, wasInitialized } = destroyFlickityIfNeeded(section);
    const grid = section.querySelector('[data-md-random-product-grid]');
    if (!grid) return;

    const activeCandidates = Array.from(grid.querySelectorAll(':scope > [data-md-random-product]')).map((node) => ({
      id: getProductId(node),
      node,
    }));
    const templateCandidates = Array.from(section.querySelectorAll('[data-md-random-product-template]')).map((template) => ({
      id: template.dataset.mdProductId || '',
      template,
    }));
    const candidates = [...activeCandidates, ...templateCandidates];
    const keep = Math.min(Number(grid.dataset.productsToShow || candidates.length), candidates.length);

    let selected = candidates.slice(0, keep);
    if (candidates.length > 1) {
      selected = shuffle(candidates.slice()).slice(0, keep);
      avoidSameFirstProduct(section, selected);
    }

    const selectedNodes = selected.map(materializeCandidate).filter(Boolean);
    grid.replaceChildren(...selectedNodes);

    injectVariantMeasurement(section);
    section.querySelectorAll('[data-md-random-product-template]').forEach((template) => template.remove());
    section.dataset.mdRandomFeaturedReady = 'true';
    reinitializeFlickity(section, carousel, wasInitialized);
  }

  function initAll(root = document) {
    if (root.matches?.('[data-md-random-featured-collection]')) init(root);
    root.querySelectorAll?.('[data-md-random-featured-collection]').forEach(init);
  }

  // This asset is loaded with defer. Execute immediately so product selection
  // completes before the later carousel-slider.js deferred script upgrades the DOM.
  initAll();

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
